import { API_BASE } from "../config/api";
import MediaUpload from "./MediaUpload";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toPicker, fromPicker } from "./eventTime";

const base = API_BASE;
const fields = [
  ["slug", "Slug dell’invito", "text"],
  ["name", "Titolo", "text"],
  ["bride", "Nome sposa", "text"],
  ["groom", "Nome sposo", "text"],
  ["location", "Luogo", "text"],
  ["date", "Data del matrimonio", "date"],
  ["premiere_start_time", "Data e ora della première", "datetime-local"],
];
const empty = Object.fromEntries(fields.map(([key]) => [key, ""]));
const labels = {
  draft: "Bozza",
  published: "Pubblicato",
  archived: "Archiviato",
};
const button =
  "rounded-lg border border-[#cbd3cc] px-5 py-3 text-sm hover:bg-[#edf0e9] disabled:opacity-50 disabled:cursor-wait";

export default function EventEditor({ token, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(""), 4000);
    return () => clearTimeout(timer);
  }, [message]);
  const [retry, setRetry] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dirty = saved
    ? fields.some(
        ([key]) =>
          form[key] !==
          (key === "premiere_start_time"
            ? toPicker(saved[key], saved.timeZone)
            : saved[key] || ""),
      )
    : fields.some(([key]) => Boolean(form[key]));

  async function request(path, method = "GET", body, signal) {
    const response = await fetch(`${base}/api/admin/events${path}`, {
      method,
      signal,
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (response.status === 401) {
      onLogout();
      throw new Error("Sessione scaduta. Accedi nuovamente.");
    }
    if (response.status === 204) return null;
    const result = await response.json().catch(() => null);
    if (!response.ok)
      throw new Error(
        result?.message ||
          result?.error ||
          (response.status === 404
            ? "Evento non trovato."
            : response.status === 403
              ? "Non hai i permessi per questa operazione."
              : "Operazione non riuscita. Controlla i dati e che lo slug non sia già utilizzato."),
      );
    if (!result?.data?.event)
      throw new Error("Risposta del servizio non valida.");
    return result.data.event;
  }
  function apply(event) {
    setSaved(event);
    setForm(
      Object.fromEntries(
        fields.map(([key]) => [
          key,
          key === "premiere_start_time"
            ? toPicker(event[key], event.timeZone)
            : event[key] || "",
        ]),
      ),
    );
  }

  useEffect(() => {
    const controller = new AbortController();
    if (!id) return () => controller.abort();
    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${base}/api/admin/events/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          cache: "no-store",
        });
        if (response.status === 401) {
          onLogout();
          return;
        }
        if (!response.ok)
          throw new Error(
            response.status === 404
              ? "Evento non trovato."
              : "Impossibile caricare l’evento.",
          );
        const data = await response.json();
        if (!data.data?.event)
          throw new Error("Risposta del servizio non valida.");
        if (!controller.signal.aborted) apply(data.data.event);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [id, token, onLogout, retry]);

  useEffect(() => {
    function warn(event) {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, uploading]);

  async function save(event) {
    event.preventDefault();
    if (busy) return;
    setError("");
    setMessage("");
    setBusy(true);
    try {
      const start = fromPicker(
        form.premiere_start_time,
        saved?.timeZone || "Europe/Rome",
        saved?.premiere_start_time,
      );
      const body = {
        ...form,
        premiere_start_time: start,
      };
      const result = await request(
        id ? `/${id}` : "",
        id ? "PATCH" : "POST",
        body,
      );
      apply(result);
      if (!id) navigate(`/admin/events/${result._id}`, { replace: true });
      else setMessage("Modifiche salvate.");
    } catch (failure) {
      setError(
        failure instanceof TypeError
          ? "Servizio non raggiungibile. Riprova."
          : failure.message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function action(type) {
    if (busy) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await request(
        type === "delete" ? `/${id}` : `/${id}/${type}`,
        type === "delete" ? "DELETE" : "POST",
      );
      if (type === "delete") {
        navigate("/admin/events", { replace: true });
        return;
      }
      apply(result);
      setMessage(
        type === "publish" ? "Evento pubblicato." : "Evento archiviato.",
      );
    } catch (failure) {
      setError(
        failure instanceof TypeError
          ? "Servizio non raggiungibile. Riprova."
          : failure.message,
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p role="status">Caricamento evento…</p>;
  if (id && !saved)
    return (
      <div role="alert">
        <p>{error}</p>
        <button
          className={`${button} mt-4`}
          onClick={() => setRetry((value) => value + 1)}
        >
          Riprova
        </button>
        <Link className="ml-4 underline" to="/admin/events">
          Torna agli eventi
        </Link>
      </div>
    );
  return (
    <div className="mx-auto max-w-4xl">
      <Link
        to="/admin/events"
        className="text-sm underline underline-offset-4"
        onClick={(event) => {
          if (
            dirty &&
            !window.confirm(
              "Lasciare la pagina e perdere le modifiche non salvate?",
            )
          )
            event.preventDefault();
        }}
      >
        ← Tutti gli eventi
      </Link>
      <header className="my-7">
        <p className="text-xs uppercase tracking-[0.2em] text-[#687674]">
          {labels[saved?.status] || "Nuova bozza"}
        </p>
        <h1 className="mt-3 font-serif text-4xl">
          {id ? "Modifica evento" : "Crea un evento"}
        </h1>
        <p className="mt-3 text-sm text-[#687674]">
          {id
            ? "Aggiorna i dettagli dell’invito e della première."
            : "Per iniziare basta uno slug. Puoi completare gli altri dettagli in seguito."}
        </p>
      </header>
      {error && (
        <p role="alert" className="mb-5 rounded-lg bg-red-50 p-4 text-red-800">
          {error}
        </p>
      )}
      {message && (
        <div className="fixed right-4 top-4 z-50 flex w-[calc(100%-2rem)] max-w-sm items-start gap-3 rounded-xl border border-stone-200 bg-white p-4 text-stone-800 shadow-lg sm:right-6 sm:top-6">
          <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-100 text-stone-700">✓</span>
          <p role="status" aria-live="polite" aria-atomic="true" className="flex-1 self-center text-sm font-medium">{message}</p>
          <button type="button" aria-label="Chiudi notifica" onClick={() => setMessage("")} className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-xl text-stone-500 hover:bg-stone-100 hover:text-stone-900">×</button>
        </div>
      )}
      <form
        onSubmit={save}
        className="rounded-xl border border-[#e1e5de] bg-white p-6 md:p-8"
      >
        <fieldset disabled={busy || uploading} className="grid gap-6 sm:grid-cols-2">
          {fields.map(([key, label, type]) => (
            <div
              key={key}
              className={
                key === "videoUrl" || key === "coverImage"
                  ? "sm:col-span-2"
                  : ""
              }
            >
              <label
                className="mb-2 block text-sm font-medium"
                htmlFor={`event-${key}`}
              >
                {label}
                {key === "slug" ? " *" : ""}
              </label>
              <input
                id={`event-${key}`}
                type={type}
                required={key === "slug"}
                pattern={key === "slug" ? "[a-z0-9]+(-[a-z0-9]+)*" : undefined}
                placeholder={key === "slug" ? "anna-marco" : undefined}
                value={form[key]}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    [key]: event.target.value,
                  }))
                }
                className="w-full rounded-lg border border-[#cbd3cc] px-3 py-3 text-sm"
              />
            </div>
          ))}
        </fieldset>
        <p className="mt-5 text-sm text-[#687674]">
          Fuso dell’evento: {saved?.timeZone || "Europe/Rome"}. Inserisci il
          video e copertina con i pulsanti di caricamento disponibili dopo aver creato la bozza.
        </p>
        <button
          disabled={busy || uploading}
          className="mt-6 rounded-lg bg-[#263b3e] px-6 py-3 text-white disabled:opacity-50"
          type="submit"
        >
          {busy
            ? "Operazione in corso…"
            : id
              ? "Salva modifiche"
              : "Crea bozza"}
        </button>
        {dirty && (
          <span className="ml-3 text-sm text-[#687674]">
            Modifiche non salvate
          </span>
        )}
      </form>
      {saved ? <section aria-label="Caricamento file" className="mt-6 space-y-4 rounded-xl border border-[#e1e5de] bg-white p-6">
        <h2 className="font-serif text-2xl">Video e copertina</h2>
        <p className="text-sm text-[#687674]">Salva le modifiche prima di caricare un file. Video MP4 H.264/AAC pronti per il web.</p>
        {["cover", "video"].map(kind => <MediaUpload key={kind} id={id} kind={kind} token={token}
          pendingCleanup={Boolean(saved.mediaCleanupKeys?.length)} published={saved.status === "published"} hasFile={Boolean(saved[kind === "video" ? "videoKey" : "coverKey"])}
          disabled={busy || uploading || dirty} onBusy={setUploading}
          onComplete={event => { apply(event); setMessage("Media aggiornati."); }} onLogout={onLogout} />)}
      </section> : <p className="mt-5 text-sm">Crea la bozza per caricare video e copertina.</p>}
      {saved && (
        <section
          aria-label="Azioni evento"
          className="mt-6 rounded-xl border border-[#e1e5de] bg-white p-6"
        >
          <h2 className="font-serif text-2xl">Pubblicazione e gestione</h2>
          <p className="mt-2 text-sm text-[#687674]">
            Salva le modifiche prima di cambiare lo stato. Per pubblicare
            servono nomi, titolo, luogo, data, orario e video.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {saved.status === "draft" && (
              <button
                disabled={busy || uploading || dirty}
                className={button}
                onClick={() => action("publish")}
              >
                Pubblica evento
              </button>
            )}
            {saved.status !== "archived" && (
              <button
                disabled={busy || uploading || dirty}
                className={button}
                onClick={() => {
                  if (
                    window.confirm(
                      "Archiviare l’evento? L’invito non sarà più disponibile al pubblico.",
                    )
                  )
                    action("archive");
                }}
              >
                Archivia evento
              </button>
            )}
            {saved.status === "published" && (
              <Link
                className={button}
                to={`/${encodeURIComponent(saved.slug)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Apri invito ↗
              </Link>
            )}
            <button
              disabled={busy || uploading}
              className={`${button} text-red-800`}
              onClick={() => setConfirmDelete(true)}
            >
              Elimina evento
            </button>
          </div>
          {confirmDelete && (
            <div
              className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4"
              role="group"
              aria-label="Conferma eliminazione"
            >
              <p>
                Eliminare definitivamente “{saved.name || saved.slug}”? Questa
                operazione non può essere annullata.
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  autoFocus
                  disabled={busy || uploading}
                  className={button}
                  onClick={() => setConfirmDelete(false)}
                >
                  Annulla
                </button>
                <button
                  disabled={busy || uploading}
                  className={`${button} text-red-800`}
                  onClick={() => action("delete")}
                >
                  Conferma eliminazione
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}







